import React, { useCallback, useEffect, useState } from 'react';
import { storageBoxApi, isPreviewableImage, isPreviewableVideo } from '../api/storageBoxApi';
import { useStorageBox } from '../StorageBoxContext';
import { fetchAndDecryptLockFile, revokeObjectUrl } from '../utils/lockFileAccess';
import DriveItemIcon from './DriveItemIcon';
import VideoFrameThumbnail from './VideoFrameThumbnail';

export default function DriveItemThumbnail({
  item,
  publicToken,
  lockSessionToken,
  variant = 'grid',
  className = '',
}) {
  const { getLockMasterKeyBytes, lockMasterKeyReady } = useStorageBox();
  const [failed, setFailed] = useState(false);
  const [lockBlobUrl, setLockBlobUrl] = useState('');
  const onVideoFail = useCallback(() => setFailed(true), []);

  useEffect(() => {
    if (!item || item.type !== 'file' || !lockSessionToken || !item.isEncrypted) {
      setLockBlobUrl('');
      return undefined;
    }
    if (isPreviewableVideo(item.mimeType, item.name)) {
      setLockBlobUrl('');
      return undefined;
    }
    if (!lockMasterKeyReady) return undefined;
    const masterKey = getLockMasterKeyBytes();
    if (!masterKey) return undefined;
    let activeUrl = '';
    let cancelled = false;
    fetchAndDecryptLockFile(item, lockSessionToken, masterKey)
      .then(({ blob }) => {
        if (cancelled) return;
        activeUrl = URL.createObjectURL(blob);
        setLockBlobUrl(activeUrl);
      })
      .catch(() => {
        if (!cancelled) setLockBlobUrl('');
      });
    return () => {
      cancelled = true;
      revokeObjectUrl(activeUrl);
      setLockBlobUrl('');
    };
  }, [item, lockSessionToken, getLockMasterKeyBytes, lockMasterKeyReady]);

  if (!item) return null;

  const rootClass = ['sb-drive-thumb', `sb-drive-thumb--${variant}`, className].filter(Boolean).join(' ');

  if (item.type === 'folder') {
    return (
      <div className={`${rootClass} sb-drive-thumb--folder`} aria-hidden>
        <DriveItemIcon item={item} className={variant === 'grid' ? 'sb-drive-card__icon' : 'sb-drive-list-thumb__icon'} />
      </div>
    );
  }

  const url = lockBlobUrl || (item.isEncrypted ? '' : storageBoxApi.downloadUrl(item.itemId, { publicToken }));
  const image = isPreviewableImage(item.mimeType, item.name);
  const video = isPreviewableVideo(item.mimeType, item.name);
  const encryptedPending = Boolean(item.isEncrypted && lockSessionToken && !lockBlobUrl);

  if (encryptedPending && (image || video)) {
    return (
      <div className={`${rootClass} sb-drive-thumb--icon`} aria-hidden>
        <DriveItemIcon item={item} className={variant === 'grid' ? 'sb-drive-card__icon' : 'sb-drive-list-thumb__icon'} />
      </div>
    );
  }

  if (!failed && image && url) {
    return (
      <div className={rootClass} aria-hidden>
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  if (!failed && video && url) {
    return (
      <div className={rootClass} aria-hidden>
        <VideoFrameThumbnail
          src={url}
          className="sb-drive-thumb__video-poster"
          onFail={onVideoFail}
        />
      </div>
    );
  }

  return (
    <div className={`${rootClass} sb-drive-thumb--icon`} aria-hidden>
      <DriveItemIcon item={item} className={variant === 'grid' ? 'sb-drive-card__icon' : 'sb-drive-list-thumb__icon'} />
    </div>
  );
}
